import * as React from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import styled from 'styled-components';
import { AlertDialog } from '../composites/AlertDialog';
import type { AlertDialogVariant } from '../composites/AlertDialog';
import { rem } from '../tokens/spacing';
import { palette } from '../tokens/palette';
import { wealthlaneAlert } from './niceModal';
import { handleConfirmError } from './confirmErrorHandler';

export interface ConfirmDialogProps {
  title?: string;
  description?: string;
  actionLabel?: string;
  cancelLabel?: string;

  variant?: AlertDialogVariant;
  subject?: string;
  /** When true, Title and Description auto-generate from variant + subject */
  preset?: boolean;

  remarksRequired?: boolean;
  remarksLabel?: string;
  defaultRemarks?: string;

  /** Called when user clicks Action. Receives remarks when remarksRequired. */
  onAction?: (remarks?: string) => void | Promise<void>;

  semanticId?: string;

  /** Structured items rendered as a list below the description. */
  items?: Array<{ label: string; value: string }>;
  /** Column headers for items (when items represent tabular data). */
  itemsHeader?: { label: string; value: string };
  /** Max height for the items area (enables scrolling). */
  itemsMaxHeight?: string;
}

export interface ConfirmDialogResult {
  confirmed: boolean;
  remarks?: string;
}

const ItemsContainer = styled.div<{ $maxHeight?: string }>`
  margin-top: ${rem(12)};
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  ${({ $maxHeight }) => $maxHeight && `max-height: ${$maxHeight}; overflow-y: auto;`}

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-track {
    background: var(--ui-background-muted, ${palette.OffWhite});
  }
  &::-webkit-scrollbar-thumb {
    background: var(--ui-border, ${palette.LightGray});
    border-radius: ${rem(4)};
  }
`;

const ItemsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${rem(32)};
  font-size: ${rem(16)};
  line-height: ${rem(18)};
  color: var(--ui-text-muted, ${palette.Gray});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
`;

const ItemsHeaderLabel = styled.span`
  font-weight: 600;
  color: var(--ui-text, ${palette.Black});
`;

export const ConfirmDialog = NiceModal.create((props: ConfirmDialogProps) => {
  const modal = useModal();
  const [loading, setLoading] = React.useState(false);
  const [remarks, setRemarks] = React.useState(props.defaultRemarks ?? '');

  const handleCancel = () => {
    modal.resolve({ confirmed: false } satisfies ConfirmDialogResult);
    void modal.hide();
  };

  const handleAction = async () => {
    if (props.onAction) {
      setLoading(true);
      try {
        await props.onAction(props.remarksRequired ? remarks : undefined);
      } catch (error) {
        setLoading(false);
        handleConfirmError(error);
        modal.resolve({ confirmed: false } satisfies ConfirmDialogResult);
        void modal.hide();
        return;
      }
      setLoading(false);
    }
    modal.resolve({
      confirmed: true,
      remarks: props.remarksRequired ? remarks : undefined,
    } satisfies ConfirmDialogResult);
    void modal.hide();
  };

  const isSingleButton = props.variant === 'single' || props.variant === 'viewRemarks';

  return (
    <AlertDialog.Root
      {...wealthlaneAlert(modal)}
      variant={props.variant}
      subject={props.subject}
      remarksRequired={props.remarksRequired}
      remarks={remarks}
      onRemarksChange={setRemarks}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay />
        <AlertDialog.Content semanticId={props.semanticId}>
          <AlertDialog.Title preset={props.preset}>
            {props.title}
          </AlertDialog.Title>

          {(props.description || props.preset) && (
            <AlertDialog.Description preset={props.preset}>
              {props.description}
            </AlertDialog.Description>
          )}

          {props.items != null && props.items.length > 0 && (
            <ItemsContainer $maxHeight={props.itemsMaxHeight}>
              {props.itemsHeader != null && (
                <ItemsRow>
                  <ItemsHeaderLabel>{props.itemsHeader.label}</ItemsHeaderLabel>
                  <ItemsHeaderLabel>{props.itemsHeader.value}</ItemsHeaderLabel>
                </ItemsRow>
              )}
              {props.items.map((item, i) => (
                <ItemsRow key={i}>
                  <span>{item.label}</span>
                  <span>{item.value}</span>
                </ItemsRow>
              ))}
            </ItemsContainer>
          )}

          {props.remarksRequired && (
            <AlertDialog.Remarks label={props.remarksLabel} />
          )}

          {props.variant === 'viewRemarks' && props.defaultRemarks && (
            <AlertDialog.Remarks readOnly label={props.remarksLabel} />
          )}

          <AlertDialog.Footer>
            {!isSingleButton && (
              <AlertDialog.Cancel semanticId="modalCancel" onClick={handleCancel}>
                {props.cancelLabel}
              </AlertDialog.Cancel>
            )}
            <AlertDialog.Action
              semanticId="modalConfirm"
              onClick={handleAction}
              closeOnClick={false}
              loading={loading}
            >
              {props.actionLabel}
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
});
